import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import type { BlowoutWeek, SeasonArchive, Team } from "@averagejoesff/shared";
import { upsertWeek } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface EditWeekFormProps {
  season: number;
  teams: Team[];
  weeks: BlowoutWeek[];
  idToken: string;
  onSaved: (archive: SeasonArchive) => void;
  /** Extra control(s) rendered at the end of the action row, right-aligned. */
  children?: ReactNode;
}

/** The next week with no result yet, or the last week if the season is full. */
function defaultWeek(weeks: BlowoutWeek[]): number {
  const unplayed = weeks.find((w) => w.status === "not_started");
  return unplayed?.week ?? weeks.at(-1)?.week ?? 1;
}

export function EditWeekForm({
  season,
  teams,
  weeks,
  idToken,
  onSaved,
  children,
}: EditWeekFormProps) {
  const [week, setWeek] = useState(defaultWeek(weeks));
  const [winnerTeam, setWinnerTeam] = useState(teams[0]?.name ?? "");
  const [winnerPoints, setWinnerPoints] = useState("");
  const [loserTeam, setLoserTeam] = useState(teams[1]?.name ?? teams[0]?.name ?? "");
  const [loserPoints, setLoserPoints] = useState("");
  const { status, error, submit } = useAdminForm(
    () =>
      upsertWeek(
        season,
        week,
        {
          winner: { team: winnerTeam, points: Number(winnerPoints) },
          loser: { team: loserTeam, points: Number(loserPoints) },
        },
        idToken,
      ),
    onSaved,
  );

  function loadWeek(w: number) {
    setWeek(w);
    const existing = weeks.find((entry) => entry.week === w);
    if (existing?.winner && existing.loser) {
      setWinnerTeam(existing.winner.name);
      setWinnerPoints(String(existing.winner.points));
      setLoserTeam(existing.loser.name);
      setLoserPoints(String(existing.loser.points));
    } else {
      setWinnerPoints("");
      setLoserPoints("");
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  if (teams.length < 2) {
    return (
      <p className="admin-panel__hint">
        Add at least two teams below before entering a week's result.
      </p>
    );
  }

  return (
    <form className="admin-panel__form" onSubmit={handleSubmit}>
      <div className="admin-panel__field">
        <label htmlFor="edit-week">Week</label>
        <select
          id="edit-week"
          value={week}
          onChange={(e) => loadWeek(Number(e.target.value))}
        >
          {weeks.map((w) => (
            <option key={w.week} value={w.week}>
              Week {w.week}
              {w.status === "not_started" ? " — not played yet" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="admin-panel__row">
        <div className="admin-panel__field">
          <label htmlFor="winner-team">Winner</label>
          <select id="winner-team" value={winnerTeam} onChange={(e) => setWinnerTeam(e.target.value)}>
            {teams.map((t) => (
              <option key={t.teamKey} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-panel__field admin-panel__field--narrow">
          <label htmlFor="winner-points">Score</label>
          <input
            id="winner-points"
            type="number"
            step="0.01"
            required
            value={winnerPoints}
            onChange={(e) => setWinnerPoints(e.target.value)}
          />
        </div>
      </div>

      <div className="admin-panel__row">
        <div className="admin-panel__field">
          <label htmlFor="loser-team">Beat</label>
          <select id="loser-team" value={loserTeam} onChange={(e) => setLoserTeam(e.target.value)}>
            {teams.map((t) => (
              <option key={t.teamKey} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-panel__field admin-panel__field--narrow">
          <label htmlFor="loser-points">Score</label>
          <input
            id="loser-points"
            type="number"
            step="0.01"
            required
            value={loserPoints}
            onChange={(e) => setLoserPoints(e.target.value)}
          />
        </div>
      </div>

      <div className="admin-panel__actions">
        <button type="submit" disabled={status === "pending"}>
          {status === "pending" ? "Saving…" : "Save Week"}
        </button>
        <AdminFormStatus
          status={status}
          error={error}
          savedLabel="Saved! Other viewers may take up to a minute to see it."
        />
        {children}
      </div>
    </form>
  );
}
